import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/shared/ThemeToggle';
import NavigationMenu from '@/components/shared/Menu';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';

/**
 * Features minimalist design with wrestling team branding and theme support.
 */
const Login: React.FC = () => {
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const { isDark } = useTheme();

    /**
     * Handle form submission with proper error handling and JWT authentication
     */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Make real API call to authenticate
            const response = await fetch(getApiUrl('/api/token/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    password: password,
                }),
            });

            const result = await safeJsonParse<{ access: string; refresh: string }>(response, 'user login');

            if (!result.success) {
                throw new Error(result.error || 'Authentication failed');
            }

            // Store authentication tokens
            localStorage.setItem('access', result.data!.access);
            localStorage.setItem('refresh', result.data!.refresh);

            // Fetch user info using the access token
            const userResponse = await fetch(getApiUrl('/api/users/me/'), {
                headers: {
                    'Authorization': `Bearer ${result.data!.access}`,
                    'Content-Type': 'application/json',
                },
            });

            const userResult = await safeJsonParse<{ role: string }>(userResponse, 'fetching user info');

            if (userResult.success) {
                localStorage.setItem('user_info', JSON.stringify(userResult.data));

                // Check if there's a stored redirect path
                const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');
                if (redirectAfterLogin) {
                    sessionStorage.removeItem('redirectAfterLogin');
                    navigate(redirectAfterLogin);
                    return;
                }

                // Redirect based on user role
                // All users go to unified dashboard except admins
                if (userResult.data!.role === 'admin') {
                    navigate('/admin-dashboard');
                } else {
                    // Athletes and coaches go to unified dashboard
                    // Dashboard will intelligently show the right view
                    navigate('/dashboard');
                }
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
            {/* Navigation Menu - Fixed Position */}
            <div className="fixed top-4 left-4 z-50">
                <NavigationMenu userInfo={null} isDark={isDark} showNavigation={true} className="my-auto" />
            </div>

            {/* Theme Toggle - Fixed Position */}
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="max-w-md w-full space-y-8">
                {/* Header with Wrestling Logo */}
                <div className="text-center">
                    <img
                        className="mx-auto h-24 w-auto mb-8 transition-all duration-200 hover:scale-110 cursor-pointer"
                        src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                        alt="Wrestling Team Logo"
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            // Fallback to PNG if SVG fails
                            (e.target as HTMLImageElement).src = isDark
                                ? "/logos/LOGO-MAIN-WHITE.png"
                                : "/logos/LOGO-MAIN-BLK.png";
                        }}
                    />
                    <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                        Welcome Back
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        CGM Sports AI Dashboard
                    </p>
                </div>

                {/* Login Form */}
                <div className="cgm-card p-8">
                    {error && (
                        <div className="cgm-alert-error px-4 py-3 rounded-md mb-6 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium cgm-text-secondary mb-2">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                                className="cgm-input w-full px-4 py-3"
                                placeholder="Enter your email"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium cgm-text-secondary mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                                className="cgm-input w-full px-4 py-3"
                                placeholder="Enter your password"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="cgm-button w-full mt-8 py-3 px-4 flex items-center justify-center"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                                    Signing In...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>

                    {/* Navigation Links */}
                    <div className="mt-6 space-y-4 text-center">
                        <p className="cgm-text-muted text-sm">
                            <Link
                                to="/password-reset"
                                className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                            >
                                Forgot Password?
                            </Link>
                        </p>

                        <p className="cgm-text-muted text-sm">
                            Don't have an account?{' '}
                            <Link
                                to="/register"
                                className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                            >
                                Register here
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
